import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { createAdminSessionId, clearAdminSessionId } from "@/lib/panelMeta";

const AuthContext = createContext(null);

function storeToken(token, remember) {
  if (remember) {
    localStorage.setItem("token", token);
    sessionStorage.removeItem("token");
  } else {
    sessionStorage.setItem("token", token);
    localStorage.removeItem("token");
  }
}

function clearTokens() {
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
  clearAdminSessionId();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const hasToken =
    typeof window !== "undefined" &&
    Boolean(localStorage.getItem("token") || sessionStorage.getItem("token"));
  // Optimistic: if token exists, don't block UI with null user spinner
  const [loading, setLoading] = useState(hasToken);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    clearTokens();
    setUser(false);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Placeholder until /auth/me resolves — ProtectedRoute allows children with token
    setUser((prev) => prev ?? { id: "pending", username: "…" });
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!sessionStorage.getItem("admin_sid")) {
          createAdminSessionId();
        }
        setUser(data);
      })
      .catch(() => {
        clearTokens();
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // Auto-logout when JWT expires (poll)
  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) return undefined;

    let expMs = 0;
    try {
      const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
      expMs = (payload.exp || 0) * 1000;
    } catch {
      return undefined;
    }

    const tick = () => {
      if (expMs && Date.now() >= expMs) {
        logout();
      }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [user, logout]);

  const login = async (username, password, remember) => {
    const { data } = await api.post("/auth/login", { username, password, remember });
    if (data.requires_2fa) {
      return { requires2fa: true, pendingToken: data.pending_token, remember, user: data.user };
    }
    storeToken(data.token, remember);
    createAdminSessionId();
    setUser(data.user);
    return { requires2fa: false, user: data.user, session_minutes: data.session_minutes };
  };

  const login2fa = async (pendingToken, code, remember) => {
    const { data } = await api.post("/auth/login/2fa", { pending_token: pendingToken, code });
    storeToken(data.token, remember);
    createAdminSessionId();
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, login2fa, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
